import { logError } from '@/shared/lib/logging';

/**
 * Generates a fresh reCAPTCHA token for client-side verification
 * @param action - The action identifier for this reCAPTCHA execution
 * @param siteKey - The reCAPTCHA site key from environment variables
 * @returns Promise<string | null> - The reCAPTCHA token or null if failed
 */
export async function generateRecaptchaToken(
    action: string,
    siteKey?: string,
): Promise<string | null> {
    if (typeof window === 'undefined') {
        logError('reCAPTCHA can only be executed in browser environment');

        return null;
    }

    if (!window.grecaptcha) {
        logError(
            'reCAPTCHA library not loaded. Make sure RecaptchaProvider is wrapped around your app.',
        );

        return null;
    }

    const recaptchaSiteKey =
        siteKey ||
        (typeof process !== 'undefined'
            ? process.env.ALGOTECH_RECAPTCHA_SITE_KEY
            : undefined);

    if (!recaptchaSiteKey) {
        logError('ALGOTECH_RECAPTCHA_SITE_KEY is not defined');

        return null;
    }

    try {
        // Wait for reCAPTCHA to be ready
        await new Promise<void>((resolve) => {
            window.grecaptcha.ready(() => resolve());
        });

        // Execute reCAPTCHA and get token
        return await window.grecaptcha.execute(recaptchaSiteKey, {
            action,
        });
    } catch (error) {
        logError('reCAPTCHA execution failed:', error);

        return null;
    }
}
